
import React, { useState, useEffect, useMemo } from 'react';
import { FloorPlanEditor } from './components/FloorPlanEditor';
import { CustomerView } from './components/CustomerView';
import { AdminSettings } from './components/AdminSettings';
import { AdminReservationList } from './components/AdminReservationList';
import { AdminReservationHistory } from './components/AdminReservationHistory';
import { AdminCalendarView } from './components/AdminCalendarView';
import { AuthModal } from './components/AuthModal';
import { INITIAL_TABLES, INITIAL_RESERVATIONS, INITIAL_ZONES, INITIAL_DIMENSIONS, INITIAL_CONFIG, DEFAULT_CATEGORIES } from './constants';
import { Table, Reservation, UserRole, Zone, FloorDimensions, RestaurantConfig, CategoryDefinition, User, WaitlistEntry } from './types';
import { DemoLayout } from './templates/demo/Layout';

const STORAGE_KEYS = {
  TABLES: 'cafeflow_tables',
  ZONES: 'cafeflow_zones',
  DIMENSIONS: 'cafeflow_dimensions',
  RESERVATIONS: 'cafeflow_reservations',
  CONFIG: 'cafeflow_config',
  CATEGORIES: 'cafeflow_categories',
  USER: 'cafeflow_current_user',
  WAITLIST: 'cafeflow_waitlist',
  WAITLIST_HISTORY: 'cafeflow_waitlist_history'
};

type AdminView = 'EDITOR' | 'RESERVATIONS' | 'CALENDAR' | 'HISTORY';

export default function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
     const saved = localStorage.getItem(STORAGE_KEYS.USER);
     return saved ? JSON.parse(saved) : null;
  });
  
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [adminView, setAdminView] = useState<AdminView>('EDITOR');
  const [showSettings, setShowSettings] = useState(false);
  const [scrollToTableId, setScrollToTableId] = useState<string | null>(null);

  const [config, setConfig] = useState<RestaurantConfig>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CONFIG);
    return saved ? JSON.parse(saved) : INITIAL_CONFIG;
  });

  const [categories, setCategories] = useState<CategoryDefinition[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CATEGORIES);
    return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
  });

  const [tables, setTables] = useState<Table[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TABLES);
    return saved ? JSON.parse(saved) : INITIAL_TABLES;
  });

  const [zones, setZones] = useState<Zone[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ZONES);
    return saved ? JSON.parse(saved) : INITIAL_ZONES;
  });

  const [dimensions, setDimensions] = useState<FloorDimensions>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DIMENSIONS);
    return saved ? JSON.parse(saved) : INITIAL_DIMENSIONS;
  });

  const [reservations, setReservations] = useState<Reservation[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.RESERVATIONS);
    return saved ? JSON.parse(saved) : INITIAL_RESERVATIONS;
  });

  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.WAITLIST);
    return saved ? JSON.parse(saved) : [];
  });

  const [waitlistHistory, setWaitlistHistory] = useState<WaitlistEntry[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.WAITLIST_HISTORY);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(config)); }, [config]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(categories)); }, [categories]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(tables)); }, [tables]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.ZONES, JSON.stringify(zones)); }, [zones]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.DIMENSIONS, JSON.stringify(dimensions)); }, [dimensions]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.RESERVATIONS, JSON.stringify(reservations)); }, [reservations]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.WAITLIST, JSON.stringify(waitlist)); }, [waitlist]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.WAITLIST_HISTORY, JSON.stringify(waitlistHistory)); }, [waitlistHistory]);
  
  useEffect(() => {
    if (currentUser) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(currentUser));
    } else {
        localStorage.removeItem(STORAGE_KEYS.USER);
    }
  }, [currentUser]);

  const handleReservation = (newRes: Reservation) => {
    setReservations(prev => [...prev, newRes]);
  };

  const handleUpdateReservation = (updatedRes: Reservation) => {
    setReservations(prev => prev.map(r => r.id === updatedRes.id ? updatedRes : r));
  };

  const handleCancelReservation = (id: string) => {
    setReservations(prev => prev.filter(r => r.id !== id));
  };

  const handleCancelSeries = (seriesId: string) => {
    setReservations(prev => prev.filter(r => r.seriesId !== seriesId));
  };

  const handleGuestCancel = (id: string) => {
    setReservations(prev => {
      const target = prev.find(r => r.id === id);
      if (!target) return prev;
      if (target.status === 'PENDING') return prev.filter(r => r.id !== id);
      return prev.map(r => r.id === id ? { ...r, status: 'CANCELLED_BY_GUEST' } : r);
    });
  };

  const handleReset = () => {
    if (window.confirm('Möchten Sie den Grundriss und alle Einstellungen wirklich auf den Standard zurücksetzen?')) {
      setTables(INITIAL_TABLES);
      setZones(INITIAL_ZONES);
      setDimensions(INITIAL_DIMENSIONS);
      setReservations(INITIAL_RESERVATIONS);
      setConfig(INITIAL_CONFIG);
      setCategories(DEFAULT_CATEGORIES);
      setWaitlist([]);
      setWaitlistHistory([]);
      const userBackup = localStorage.getItem(STORAGE_KEYS.USER);
      Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
      if (userBackup) localStorage.setItem(STORAGE_KEYS.USER, userBackup);
    }
  };

  const handleLogin = (user: User) => {
    setCurrentUser(user);
    setIsGuestMode(false);
    setShowAuthModal(false);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsGuestMode(false);
  };

  const handleTableNavigate = (tableId: string) => {
    setScrollToTableId(tableId);
    setAdminView('RESERVATIONS');
  };

  const isAdmin = useMemo(() => currentUser?.role === UserRole.ADMIN, [currentUser]);
  const isLoggedIn = useMemo(() => currentUser !== null || isGuestMode, [currentUser, isGuestMode]);
  
  const pendingCount = reservations.filter(r => r.status === 'PENDING').length;
  const cancelledByGuestCount = reservations.filter(r => r.status === 'CANCELLED_BY_GUEST').length;
  const adminNotificationCount = pendingCount + cancelledByGuestCount;

  return (
    <DemoLayout
      config={config}
      currentUser={currentUser}
      isAdmin={isAdmin}
      isLoggedIn={isLoggedIn}
      adminView={adminView}
      setAdminView={setAdminView}
      adminNotificationCount={adminNotificationCount}
      onLogout={handleLogout}
      onShowSettings={() => setShowSettings(true)}
    >
      {isAdmin && currentUser ? (
         <>
            {adminView === 'EDITOR' && (
              <FloorPlanEditor 
                tables={tables} onUpdateTables={setTables}
                zones={zones} onUpdateZones={setZones}
                dimensions={dimensions} onUpdateDimensions={setDimensions}
                categories={categories} reservations={reservations}
                config={config} onReset={handleReset} onTableClick={handleTableNavigate}
              />
            )}
            {adminView === 'RESERVATIONS' && (
              <AdminReservationList 
                reservations={reservations} tables={tables} zones={zones} categories={categories}
                onMakeReservation={handleReservation} onUpdateReservation={handleUpdateReservation}
                onCancelReservation={handleCancelReservation} onCancelSeries={handleCancelSeries}
                config={config} scrollToTableId={scrollToTableId} onScrollComplete={() => setScrollToTableId(null)}
              />
            )}
            {adminView === 'CALENDAR' && (
              <AdminCalendarView 
                reservations={reservations} tables={tables} categories={categories}
                onEditReservation={handleUpdateReservation} onMakeReservation={handleReservation} config={config}
              />
            )}
            {adminView === 'HISTORY' && (
              <AdminReservationHistory reservations={reservations} tables={tables} zones={zones} categories={categories} />
            )}
         </>
      ) : (
        <CustomerView 
          tables={tables} reservations={reservations} onMakeReservation={handleReservation}
          onCancelReservation={handleGuestCancel} zones={zones} dimensions={dimensions}
          config={config} categories={categories} currentUser={currentUser}
          isGuestMode={isGuestMode} setIsGuestMode={setIsGuestMode}
          onLogin={handleLogin} onLoginRequest={() => setShowAuthModal(true)}
        />
      )}

      {showSettings && (
        <AdminSettings 
          config={config} categories={categories} onUpdateConfig={setConfig}
          onUpdateCategories={setCategories} onClose={() => setShowSettings(false)}
        />
      )}

      {showAuthModal && (
        <AuthModal onLogin={handleLogin} onClose={() => setShowAuthModal(false)} config={config} />
      )}
    </DemoLayout>
  );
}
